/**
 * CRM (lot 19) display helpers: French labels of every enum, stage defaults,
 * Paris calendar helpers, journal lines and the internal API calls of the
 * `/internal/crm/*` routes.
 */
import {
  openStageSchema,
  euroAmountSchema,
  type ActivityResource,
  type CompanyOriginValue,
  type CompanyStatusValue,
  type ContactRoleValue,
  type DealModelValue,
  type DealStageValue,
  type DealSummaryResource,
  type LostReasonValue,
  type NextActionResource,
  type OpenStageValue,
  type ReminderResource,
  type ReminderTypeValue,
  type SystemEventResource,
} from '@voxler/contracts';
import { assertNever } from '@voxler/domain';
import type { BadgeVariant } from '@/components/ui/badge';
import { ApiHttpError, internalSend } from './api';
import { formatEurCents, NBSP } from './format';

// --- Labels -------------------------------------------------------------------

export const STAGE_LABEL: Record<DealStageValue, string> = {
  to_qualify: 'À qualifier',
  r1_planned: 'R1 planifié',
  r1_done: 'R1 fait',
  proposal_presented: 'Proposition présentée',
  specifications: 'Cahier des charges',
  signature_pending: 'Signature en attente',
  won: 'Gagnée',
  lost: 'Perdue',
};

/** Open stages, in pipeline order. */
export const OPEN_STAGES: readonly OpenStageValue[] = openStageSchema.options;

/** Default probability (percent) of each open stage. */
export const STAGE_PROBABILITY: Record<OpenStageValue, number> = {
  to_qualify: 10,
  r1_planned: 20,
  r1_done: 30,
  proposal_presented: 50,
  specifications: 70,
  signature_pending: 90,
};

/** Default subject of the next action of a deal entering a stage. */
export const STAGE_NEXT_SUBJECT: Record<OpenStageValue, string> = {
  to_qualify: 'Appeler pour caler le R1',
  r1_planned: 'Préparer le R1',
  r1_done: 'Préparer la proposition',
  proposal_presented: 'Relancer après présentation',
  specifications: 'Faire valider le cahier des charges',
  signature_pending: 'Vérifier la signature',
};

/** Open stages of a model: a shared licence skips the specifications. */
export function stagesForModel(model: DealModelValue): readonly OpenStageValue[] {
  switch (model) {
    case 'custom':
      return OPEN_STAGES;
    case 'shared_license':
      return OPEN_STAGES.filter((stage) => stage !== 'specifications');
    default:
      return assertNever(model);
  }
}

/** The open stage following `stage` for a model; null at the last one. */
export function nextOpenStage(model: DealModelValue, stage: DealStageValue): OpenStageValue | null {
  const stages = stagesForModel(model);
  const index = stages.findIndex((candidate) => candidate === stage);
  if (index < 0) {
    return null;
  }
  return stages[index + 1] ?? null;
}

/** Narrows a stage to an open one. */
export function asOpenStage(stage: DealStageValue): OpenStageValue | null {
  return OPEN_STAGES.find((candidate) => candidate === stage) ?? null;
}

export function stageBadgeVariant(stage: DealStageValue): BadgeVariant {
  switch (stage) {
    case 'won':
      return 'success';
    case 'lost':
      return 'danger';
    case 'to_qualify':
    case 'r1_planned':
    case 'r1_done':
    case 'proposal_presented':
    case 'specifications':
    case 'signature_pending':
      return 'neutral';
    default:
      return assertNever(stage);
  }
}

export const MODEL_LABEL: Record<DealModelValue, string> = {
  custom: 'Sur mesure',
  shared_license: 'Licence mutualisée',
};

export const MODEL_HINT: Record<DealModelValue, string> = {
  custom: 'Passe par l’étape Cahier des charges avant la signature.',
  shared_license: 'Va directement de la proposition à la signature.',
};

export const ORIGIN_LABEL: Record<CompanyOriginValue, string> = {
  network: 'Réseau',
  word_of_mouth: 'Bouche-à-oreille',
  existing_client: 'Client existant',
  linkedin: 'LinkedIn',
  website: 'Site web',
  other: 'Autre',
};

export const ORIGINS: readonly CompanyOriginValue[] = [
  'network',
  'word_of_mouth',
  'existing_client',
  'linkedin',
  'website',
  'other',
];

export const COMPANY_STATUS_LABEL: Record<CompanyStatusValue, string> = {
  prospect: 'Prospect',
  client: 'Client',
  former_client: 'Ancien client',
};

export const COMPANY_STATUS_BADGE: Record<CompanyStatusValue, BadgeVariant> = {
  prospect: 'info',
  client: 'success',
  former_client: 'neutral',
};

export const ROLE_LABEL: Record<ContactRoleValue, string> = {
  decider: 'Décideur',
  user: 'Utilisateur',
  billing: 'Facturation',
  tech: 'Technique',
};

export const ROLE_HINT: Record<ContactRoleValue, string> = {
  decider: 'signe et arbitre le budget',
  user: 'utilisera l’outil au quotidien',
  billing: 'reçoit factures et mandat SEPA',
  tech: 'informatique, accès, intégrations',
};

export const ROLES: readonly ContactRoleValue[] = ['decider', 'user', 'billing', 'tech'];

export const REMINDER_TYPE_LABEL: Record<ReminderTypeValue, string> = {
  call: 'Appel',
  email: 'Email',
  meeting: 'Rendez-vous',
  task: 'Tâche',
};

export const REMINDER_TYPES: readonly ReminderTypeValue[] = ['call', 'email', 'meeting', 'task'];

export const LOST_REASON_LABEL: Record<LostReasonValue, string> = {
  price: 'Prix',
  timing: 'Timing ou priorité',
  competitor: 'Concurrent retenu',
  no_need: 'Pas de besoin réel',
  no_response: 'Plus de réponse',
  other: 'Autre',
};

export const LOST_REASONS: readonly LostReasonValue[] = [
  'price',
  'timing',
  'competitor',
  'no_need',
  'no_response',
  'other',
];

export type LoggedKind = 'note' | 'call' | 'meeting' | 'email';

export const LOGGED_KINDS: readonly LoggedKind[] = ['note', 'call', 'meeting', 'email'];

export const LOGGED_KIND_LABEL: Record<LoggedKind, string> = {
  note: 'Note',
  call: 'Appel',
  meeting: 'Rendez-vous',
  email: 'Email',
};

export const LOGGED_KIND_PLACEHOLDER: Record<LoggedKind, string> = {
  note: 'Écrire une note',
  call: 'Résumer l’appel',
  meeting: 'Résumer le rendez-vous',
  email: 'Résumer l’email envoyé ou reçu',
};

/** Fields of the structured R1 report, in display order. */
export const DISCOVERY_FIELDS = [
  { key: 'context', label: 'Contexte' },
  { key: 'pains', label: 'Douleurs' },
  { key: 'currentTools', label: 'Outils actuels' },
  { key: 'decisionMaker', label: 'Décideur' },
  { key: 'budget', label: 'Budget' },
  { key: 'timeline', label: 'Échéance' },
] as const;

// --- Paris calendar -----------------------------------------------------------

const PARIS_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const PARIS_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Paris calendar date `YYYY-MM-DD` of an instant. */
export function parisDateOf(instant: Date): string {
  return PARIS_DATE.format(instant);
}

/** Today in Paris, `YYYY-MM-DD`. */
export function parisToday(): string {
  return parisDateOf(new Date());
}

function utcDateOf(date: string): Date {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Calendar date shifted by `days`. */
export function addDays(date: string, days: number): string {
  const value = utcDateOf(date);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

/** The Monday strictly after `date`. */
export function nextMonday(date: string): string {
  const weekday = utcDateOf(date).getUTCDay();
  return addDays(date, (8 - weekday) % 7 || 7);
}

/** The month after the one of `date`, `YYYY-MM`. */
export function nextMonthOf(date: string): string {
  const value = utcDateOf(date);
  value.setUTCDate(1);
  value.setUTCMonth(value.getUTCMonth() + 1);
  return value.toISOString().slice(0, 7);
}

/** `YYYY-MM-DD` → « 05/11/2026 ». */
export function formatDate(date: string): string {
  const [y, m, d] = date.slice(0, 10).split('-');
  return y === undefined || m === undefined || d === undefined ? date : `${d}/${m}/${y}`;
}

/** Instant → Paris « 05/11/2026 ». */
export function formatInstantDate(iso: string): string {
  const instant = new Date(iso);
  return Number.isNaN(instant.getTime()) ? iso : formatDate(parisDateOf(instant));
}

/** `YYYY-MM-DD` → « 5 novembre 2026 ». */
export function formatLongDate(date: string): string {
  return utcDateOf(date).toLocaleDateString('fr-FR', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** `YYYY-MM-DD` → « Mercredi 28 octobre 2026 ». */
export function formatWeekdayDate(date: string): string {
  const text = utcDateOf(date).toLocaleDateString('fr-FR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** « Aujourd'hui, 10:00 », « 29/10, 10:00 » (year added when it is not the current one). */
export function formatDue(date: string, time: string, today: string = parisToday()): string {
  if (date === today) {
    return `Aujourd’hui, ${time}`;
  }
  const [y, m, d] = date.split('-');
  const sameYear = y === today.slice(0, 4);
  return `${d ?? ''}/${m ?? ''}${sameYear ? '' : `/${y ?? ''}`}, ${time}`;
}

/** Due moment of a next action (Paris). */
export function formatNextAction(next: NextActionResource): string {
  const instant = new Date(next.dueAt);
  if (Number.isNaN(instant.getTime())) {
    return next.dueAt;
  }
  return formatDue(parisDateOf(instant), PARIS_TIME.format(instant));
}

export function formatReminderDue(reminder: ReminderResource): string {
  return formatDue(reminder.dueDate, reminder.dueTime);
}

/** « Lundi 2 novembre 2026 à 9 h 00 ». */
export function formatReminderLong(reminder: ReminderResource): string {
  const [h = '0', min = '00'] = reminder.dueTime.split(':');
  return `${formatWeekdayDate(reminder.dueDate)} à ${String(Number(h))} h ${min}`;
}

/** « Entrée aujourd'hui », « 6 jours dans l'étape ». */
export function daysInStageLabel(days: number): string {
  if (days === 0) {
    return 'Entrée aujourd’hui';
  }
  return `${days} jour${days > 1 ? 's' : ''} dans l’étape`;
}

export function daysLabel(days: number): string {
  return `${days} jour${days > 1 ? 's' : ''}`;
}

// --- Amounts ------------------------------------------------------------------

/** « 1 234,00 € », or « — » when absent. */
export function eurOrDash(value: string | null): string {
  return value === null ? '—' : formatEurCents(value);
}

export function percentLabel(value: number): string {
  return `${value}${NBSP}%`;
}

/**
 * An amount typed by the operator (« 1 200,50 », « 300 ») as the API decimal
 * string (« 1200.50 »); null when empty, undefined when malformed.
 */
export function parseEuroInput(input: string): string | null | undefined {
  const normalized = input.replace(/\s/g, '').replace(',', '.');
  if (normalized === '') {
    return null;
  }
  return euroAmountSchema.safeParse(normalized).success ? normalized : undefined;
}

/** Decimal string back to an input value (« 1200.5 » → « 1200,5 »). */
export function euroInputOf(value: string | null): string {
  return value === null ? '' : value.replace('.', ',');
}

/** Form-level check of an optional amount field. */
export function euroInputError(input: string, required = false): string | undefined {
  const parsed = parseEuroInput(input);
  if (parsed === undefined) {
    return 'Montant invalide : chiffres, virgule et jusqu’à 6 décimales, ex. 1200,50.';
  }
  if (parsed === null && required) {
    return 'Montant obligatoire.';
  }
  return undefined;
}

// --- Deals --------------------------------------------------------------------

/** Whether a deal has an amount (otherwise shown « À chiffrer »). */
export function isPriced(deal: DealSummaryResource): boolean {
  return deal.setup !== null || deal.monthly !== null;
}

// --- Journal ------------------------------------------------------------------

/** French journal line of a system event (« Étape : R1 fait vers Proposition présentée »). */
export function systemEventLine(event: SystemEventResource): string {
  switch (event.type) {
    case 'company_created':
      return 'Entreprise créée';
    case 'deal_created':
      return `Affaire créée : ${event.dealTitle}`;
    case 'stage_changed':
      return `Étape : ${STAGE_LABEL[event.from]} vers ${STAGE_LABEL[event.to]}`;
    case 'deal_won':
      return `Affaire gagnée : ${event.dealTitle}`;
    case 'deal_lost':
      return `Affaire perdue : ${event.dealTitle}, motif ${LOST_REASON_LABEL[event.reason].toLowerCase()}`;
    case 'accounts_created':
      return `Compte facturé créé : ${event.accountNames.join(' et ')}`;
    case 'reminder_created': {
      const instant = new Date(event.dueAt);
      const when = Number.isNaN(instant.getTime())
        ? event.dueAt
        : `le ${formatDate(parisDateOf(instant)).slice(0, 5)} à ${PARIS_TIME.format(instant).replace(':', ' h ')}`;
      return `Rappel créé : ${event.subject.charAt(0).toLowerCase()}${event.subject.slice(1)}, ${when}`;
    }
    case 'reminder_done':
      return `Rappel fait : ${event.subject}`;
    default:
      return assertNever(event);
  }
}

/** Title of a journal entry. */
export function activityTitle(activity: ActivityResource): string {
  const { content } = activity;
  switch (content.kind) {
    case 'note':
    case 'call':
    case 'meeting':
    case 'email':
      return LOGGED_KIND_LABEL[content.kind];
    case 'discovery':
      return 'Compte rendu de R1';
    case 'system':
      return systemEventLine(content.event);
    default:
      return assertNever(content);
  }
}

/** Plain-text R1 report, to paste into a proposal. */
export function discoveryText(
  report: Record<(typeof DISCOVERY_FIELDS)[number]['key'], string>,
): string {
  return DISCOVERY_FIELDS.map((field) => `${field.label} : ${report[field.key] || '—'}`).join('\n');
}

// --- API ----------------------------------------------------------------------

/** Query-key root of the CRM: every mutation invalidates it. */
export const CRM_KEY = ['crm'] as const;

export const crmKeys = {
  pipeline: ['crm', 'pipeline'],
  companies: ['crm', 'companies'],
  company: (id: string) => ['crm', 'company', id],
  deal: (id: string) => ['crm', 'deal', id],
  todo: ['crm', 'todo'],
  unlinked: ['crm', 'unlinked'],
} as const;

/** JSON call to the internal CRM API. */
export function crmSend<T>(method: string, path: string, body?: unknown): Promise<T> {
  return internalSend<T>(method, `/internal/crm${path}`, body);
}

/** Call answered by 204 No Content (no JSON body to read). */
export async function crmSendEmpty(method: string, path: string, body?: unknown): Promise<void> {
  await internalSend<undefined>(method, `/internal/crm${path}`, body);
}

/** Message of a failed mutation: the API's French `detail` when given. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiHttpError && error.detail !== null ? error.detail : fallback;
}
